import { useMemo } from 'react';
import { GEOLOCATION_POLICY } from '../constants';
import type { GeolocationPolicy, PunchcardConfig } from '../types';

const DEFAULT_CONFIG: PunchcardConfig = {
  writeEnabled: false,
  photoRequired: false,
  geolocation: GEOLOCATION_POLICY.FLEXIBLE,
};

const GEOLOCATION_POLICIES: ReadonlySet<unknown> = new Set(Object.values(GEOLOCATION_POLICY));

function isTrue(val: unknown): boolean {
  return val === true || (typeof val === 'string' && val.toLowerCase() === 'true');
}

function isRequired(val: unknown): boolean {
  return typeof val === 'string' && val.toLowerCase() === 'required';
}

/**
 * Reads the agreement's geolocation rule: the declared policy when it is a known one,
 * `flexible` otherwise (missing, null or unrecognised). Only an explicit, exact
 * `required` blocks punches without a precise location.
 *
 * @param val - Raw `context.punchcard.geolocation` value.
 * @returns The geolocation policy to enforce.
 */
export function parseGeolocationPolicy(val: unknown): GeolocationPolicy {
  return GEOLOCATION_POLICIES.has(val) ? (val as GeolocationPolicy) : GEOLOCATION_POLICY.FLEXIBLE;
}

/**
 * Parses the punchcard module settings from the agreement's serialized context.
 *
 * @param context - The agreement `context` JSON string.
 * @returns The punchcard config; a disabled config when the context is missing or invalid.
 */
export function parsePunchcardConfig(context: string | null | undefined): PunchcardConfig {
  if (!context) return DEFAULT_CONFIG;
  try {
    const ctxObj = JSON.parse(context) as Record<string, unknown>;
    const punchcard = (ctxObj.punchcard ?? {}) as Record<string, unknown>;
    return {
      writeEnabled: isTrue(punchcard.writeEnabled),
      photoRequired: isRequired(punchcard.photo) || isTrue(punchcard.photo),
      geolocation: parseGeolocationPolicy(punchcard.geolocation),
    };
  } catch {
    return DEFAULT_CONFIG;
  }
}

/**
 * Memoized {@link parsePunchcardConfig} for the active agreement's context.
 *
 * @param context - The agreement `context` JSON string.
 * @returns The punchcard config.
 */
export function usePunchcardConfig(context: string | null | undefined): PunchcardConfig {
  return useMemo(() => parsePunchcardConfig(context), [context]);
}
